import React, { useState, useEffect, memo } from 'react';
import styles from '../Point.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { getTickets, clearTickets } from '../../../store/actions/tickets';
import useOnScreen from '../../../hooks/useOnScreen';
import ContentSpinner from '../../shared/Spinner/Spinner';
import Title from '../../shared/Title/Title';
import Filters from './Filters/Filters';
import Table from './Table/Table';
import { getTranslation } from '../../../utils/translation';
import { TICKET_FILTER_PERIODS } from '../../../constants/defaultValues';
import Vouchers from '../Vouchers/Vouchers';

const rangePeriod = TICKET_FILTER_PERIODS.find(period => period.key === 'range');
const initialPeriod = TICKET_FILTER_PERIODS[0];

const tabs = {
    openBets: 1,
    bettingHistory: 2,
    vouchers: 3,
};

const ticketOrders = {
    unknown: 0,
    ascending: 1,
    descending: 2,
    unrecognized: -1,
};

const initialFilterOpenBets = {
    source: 0,
    status: 1,
    order: 2,
    voucherCode: '',
};

const initialFilterHistory = {
    source: 0,
    status: 0,
    voucherCode: '',
    startTime: null,
    endTime: null,
};

export default memo(function Tickets() {
    const dispatch = useDispatch();
    const { data, loading, hasMore } = useSelector(state => state.tickets);
    const { ref, isIntersecting: isVisible } = useOnScreen();

    const [selectedTab, setSelectedTab] = useState(tabs.openBets);
    const [openBets, setOpenBets] = useState(initialFilterOpenBets);
    const [bettingHistory, setBettingHistory] = useState(() => {
        const { startTime, endTime } = initialPeriod.getRange();
        return {
            ...initialFilterHistory,
            startTime,
            endTime,
        };
    });
    const [selectedPeriod, setSelectedPeriod] = useState(initialPeriod);
    const [pageNumber, setPageNumber] = useState(0);

    useEffect(() => {
        return () => clearData();
    }, []);

    useEffect(() => {
        if (!loading && isVisible && hasMore) {
            setPageNumber(page => page + 1);
        }
    }, [isVisible]);

    useEffect(() => {
        if (selectedTab === tabs.openBets) {
            dispatch(getTickets(openBets, pageNumber));
        } else if (selectedTab === tabs.bettingHistory) {
            dispatch(getTickets(bettingHistory, pageNumber));
        } else if (selectedTab === tabs.vouchers) {
            console.log('get vouchers');
        }
    }, [pageNumber]);

    const clearData = () => {
        dispatch(clearTickets());
    };

    const onTabChange = tab => {
        clearData();

        setSelectedTab(tab);

        if (tab === tabs.openBets) {
            getOpenBetTickets(openBets);
        } else if (tab === tabs.bettingHistory) {
            getBetHistoryTickets(bettingHistory);
        }
    };

    const onOpenBetsSourceChange = source => {
        setOpenBets(openBets => {
            const newOpenBets = { ...openBets, source: source.value };
            getOpenBetTickets(newOpenBets);
            return newOpenBets;
        });
    };

    const onOpenBetsOrderChange = order => {
        setOpenBets(openBets => {
            const newOpenBets = { ...openBets, order: order.value };
            getOpenBetTickets(newOpenBets);
            return newOpenBets;
        });
    };

    const onOpenBetsVoucherCodeChange = e => {
        clearData();

        const voucherCode = e.target.value;

        setOpenBets(openBets => ({ ...openBets, voucherCode }));
    };

    const onBetHistorySourceChange = source => {
        setBettingHistory(bettingHistory => {
            const newBettingHistory = { ...bettingHistory, source: source.value };
            getBetHistoryTickets(newBettingHistory);
            return newBettingHistory;
        });
    };

    const onBetHistoryVoucherCodeChange = e => {
        clearData();

        const voucherCode = e.target.value;

        setBettingHistory(bettingHistory => ({ ...bettingHistory, voucherCode }));
    };

    const onBetHistoryPeriodChange = period => {
        setBettingHistory(bettingHistory => {
            const newBettingHistory = { ...bettingHistory, ...period };
            getBetHistoryTickets(newBettingHistory);
            return newBettingHistory;
        });
    };

    const onBetHistoryStatusChange = status => {
        setBettingHistory(bettingHistory => {
            const newBettingHistory = { ...bettingHistory, status: status.value };
            getBetHistoryTickets(newBettingHistory);
            return newBettingHistory;
        });
    };

    const getOpenBetTickets = openBetFilters => {
        clearData();

        if (pageNumber > 0) {
            setPageNumber(0);
        } else {
            dispatch(getTickets(openBetFilters, 0));
        }
    };

    const getBetHistoryTickets = betHistoryFilters => {
        clearData();

        if (pageNumber > 0) {
            setPageNumber(0);
        } else {
            if (betHistoryFilters.startTime && betHistoryFilters.endTime) {
                dispatch(getTickets(betHistoryFilters, 0));
            }
        }
    };

    return (
        <>
            <Title title={getTranslation('title.tickets')} />
            <div className='filters-nav'>
                <button
                    className={`filter-tab ${selectedTab === tabs.openBets ? 'active' : ''}`}
                    onClick={() => onTabChange(tabs.openBets)}
                >
                    {getTranslation('ticketsTabsOpenBets')}
                </button>
                <button
                    className={`filter-tab ${selectedTab === tabs.bettingHistory ? 'active' : ''}`}
                    onClick={() => onTabChange(tabs.bettingHistory)}
                >
                    {getTranslation('ticketsTabsBettingHistory')}
                </button>
                <button
                    className={`filter-tab ${selectedTab === tabs.vouchers ? 'active' : ''}`}
                    onClick={() => onTabChange(tabs.vouchers)}
                >
                    {getTranslation('ticketsTabsVouchers')}
                </button>
            </div>

            {selectedTab === tabs.vouchers ? (
                <Vouchers />
            ) : (
                <>
                    <Filters
                        tabs={tabs}
                        selectedTab={selectedTab}
                        periods={TICKET_FILTER_PERIODS}
                        rangePeriod={rangePeriod}
                        selectedPeriod={selectedPeriod}
                        setSelectedPeriod={setSelectedPeriod}
                        orders={ticketOrders}
                        openBets={openBets}
                        bettingHistory={bettingHistory}
                        setBettingHistory={setBettingHistory}
                        onTabChange={onTabChange}
                        onOpenBetsSourceChange={onOpenBetsSourceChange}
                        onOpenBetsOrderChange={onOpenBetsOrderChange}
                        onOpenBetsVoucherCodeChange={onOpenBetsVoucherCodeChange}
                        onBetHistorySourceChange={onBetHistorySourceChange}
                        onBetHistoryPeriodChange={onBetHistoryPeriodChange}
                        onBetHistoryStatusChange={onBetHistoryStatusChange}
                        onBetHistoryVoucherCodeChange={onBetHistoryVoucherCodeChange}
                        getOpenBetTickets={getOpenBetTickets}
                        getBetHistoryTickets={getBetHistoryTickets}
                        clearData={clearData}
                    />
                    <main className={styles.main} style={{ height: 'calc(100% - 202px)' }}>
                        {loading && <ContentSpinner />}
                        {data.length > 0 && <Table tickets={data} />}
                        <button className={'v-hidden h-0'} ref={ref}>
                            Load More
                        </button>
                    </main>
                </>
            )}
        </>
    );
});
